import { useState } from 'react';
import { Check, X, Edit2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import type { DceRequirement } from '../../types/dce-analysis.types';
import { useUpdateDceRequirement } from '../../hooks/useDceAnalysis';
import { ConfidenceBadge } from './ConfidenceBadge';
import { SourceLink } from './SourceLink';

interface Props {
  requirement: DceRequirement;
  opportunityId: number;
  selected: boolean;
  onToggleSelect: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  technique: 'Technique',
  fonctionnelle: 'Fonctionnelle',
  administrative: 'Administrative',
  qualite: 'Qualité',
  securite: 'Sécurité',
  rgpd: 'RGPD',
  methodologique: 'Méthodologique',
};

export function RequirementCard({ requirement, opportunityId, selected, onToggleSelect }: Props) {
  const [editing, setEditing] = useState(false);
  const [statementDraft, setStatementDraft] = useState(requirement.statement);
  const [notesDraft, setNotesDraft] = useState(requirement.humanNotes ?? '');
  const updateMutation = useUpdateDceRequirement(opportunityId);

  const handleStatus = (humanStatus: 'VALIDATED' | 'REJECTED') => {
    updateMutation.mutate(
      { id: requirement.id, payload: { humanStatus } },
      {
        onError: () => toast.error('Erreur lors de la mise à jour'),
      },
    );
  };

  const handleSave = () => {
    const payload: Parameters<typeof updateMutation.mutate>[0]['payload'] = {};
    if (statementDraft !== requirement.statement) payload.statement = statementDraft;
    if (notesDraft !== (requirement.humanNotes ?? '')) payload.humanNotes = notesDraft;
    if (Object.keys(payload).length === 0) {
      setEditing(false);
      return;
    }
    updateMutation.mutate(
      { id: requirement.id, payload },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success('Exigence corrigée');
        },
        onError: () => toast.error('Erreur'),
      },
    );
  };

  return (
    <div className={`rounded-md border bg-card p-3 ${statusBg(requirement.humanStatus)}`}>
      <div className="flex items-start gap-2">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggleSelect}
          className="mt-1"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <div className="flex-1">
              {editing ? (
                <Textarea
                  value={statementDraft}
                  onChange={(e) => setStatementDraft(e.target.value)}
                  rows={2}
                  className="text-sm"
                />
              ) : (
                <p className="text-sm font-medium">{requirement.statement}</p>
              )}
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge variant="outline">{TYPE_LABELS[requirement.type] ?? requirement.type}</Badge>
                <span className={`text-[10px] font-semibold uppercase ${requirement.mandatory ? 'text-red-600' : 'text-amber-600'}`}>
                  {requirement.mandatory ? 'Obligatoire' : 'Souhaitée'}
                </span>
                {requirement.originalRef && (
                  <span className="text-xs text-muted-foreground">
                    {requirement.originalRef}
                  </span>
                )}
                <ConfidenceBadge confidence={requirement.confidence} />
                <StatusBadge status={requirement.humanStatus} />
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button
                size="sm"
                variant={requirement.humanStatus === 'VALIDATED' ? 'default' : 'outline'}
                onClick={() => handleStatus('VALIDATED')}
                disabled={updateMutation.isPending}
                className="h-7"
                title="Valider"
              >
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant={requirement.humanStatus === 'REJECTED' ? 'destructive' : 'outline'}
                onClick={() => handleStatus('REJECTED')}
                disabled={updateMutation.isPending}
                className="h-7"
                title="Rejeter"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
              {!editing && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditing(true)}
                  className="h-7"
                  title="Éditer"
                >
                  <Edit2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>

          {editing && (
            <div className="space-y-1 mb-2">
              <Textarea
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                placeholder="Note interne…"
                rows={2}
                className="text-xs"
              />
              <div className="flex gap-1">
                <Button size="sm" onClick={handleSave} className="h-7 text-xs">
                  Enregistrer
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditing(false);
                    setStatementDraft(requirement.statement);
                    setNotesDraft(requirement.humanNotes ?? '');
                  }}
                  className="h-7 text-xs"
                >
                  Annuler
                </Button>
              </div>
            </div>
          )}

          {requirement.humanNotes && !editing && (
            <p className="text-xs italic text-muted-foreground mb-1">{requirement.humanNotes}</p>
          )}

          <SourceLink
            documentId={requirement.sourceDocumentId}
            filename={requirement.sourceDocumentFilename}
            pageNumber={requirement.sourcePageNumber}
            excerpt={requirement.sourceExcerpt}
          />

          {requirement.lastUpdatedByName && (
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Modifié par {requirement.lastUpdatedByName} · {formatRelative(requirement.updatedAt)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: DceRequirement['humanStatus'] }) {
  if (status === 'EXTRACTED') return null;
  const label = { VALIDATED: 'Validé', REJECTED: 'Rejeté', CORRECTED: 'Corrigé' }[status];
  const color = {
    VALIDATED: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    REJECTED: 'bg-red-100 text-red-700 border-red-300',
    CORRECTED: 'bg-blue-100 text-blue-700 border-blue-300',
  }[status];
  return <Badge variant="outline" className={color}>{label}</Badge>;
}

function statusBg(status: DceRequirement['humanStatus']): string {
  if (status === 'VALIDATED') return 'border-emerald-300/50';
  if (status === 'REJECTED') return 'opacity-60';
  return '';
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  return new Date(iso).toLocaleDateString('fr-FR');
}
